import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, Calendar, Target, Dumbbell, TrendingUp, Info } from "lucide-react";
import type { Exercise, DaySchedule } from "@/types";

function ExerciseRow({ exercise, index }: { exercise: Exercise; index: number }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-3 pr-4">
        <div className="flex items-start gap-3">
          <span className="text-xs text-muted-foreground w-5">{index + 1}.</span>
          <div>
            <p className="font-medium">{exercise.name}</p>
            {exercise.notes && (
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {exercise.notes}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-center whitespace-nowrap">
        <span className="text-amber-500 font-medium">{exercise.sets}</span>
        <span className="text-muted-foreground"> × </span>
        <span>{exercise.reps}</span>
      </td>
      <td className="py-3 px-4 text-center">
        <span className="text-muted-foreground text-sm">{exercise.rest}</span>
      </td>
      <td className="py-3 px-4 text-center">
        <span
          className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium ${
            exercise.rpe >= 8
              ? "bg-red-500/10 text-red-400"
              : exercise.rpe >= 7
              ? "bg-yellow-500/10 text-yellow-400"
              : "bg-amber-500/10 text-amber-500"
          }`}
        >
          {exercise.rpe}
        </span>
      </td>
    </tr>
  );
}

function DayCard({ schedule }: { schedule: DaySchedule }) {
  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{schedule.day}</h3>
            <p className="text-sm text-amber-500">{schedule.focus}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Dumbbell className="w-4 h-4" />
            <span>{schedule.exercises.length} exercises</span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b border-border">
              <th className="text-left py-3 px-4 font-medium">Exercise</th>
              <th className="py-3 px-4 font-medium">Sets × Reps</th>
              <th className="py-3 px-4 font-medium">Rest</th>
              <th className="py-3 px-4 font-medium">RPE</th>
            </tr>
          </thead>
          <tbody>
            {schedule.exercises.map((exercise, key) => (
              <ExerciseRow key={key} exercise={exercise} index={key} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Plan() {
  const { plan, isLoading, generatePlan } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6">
        <div className="max-w-xl mx-auto text-center">
          <Card className="p-8">
            <Calendar className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">No Active Plan</h1>
            <p className="text-muted-foreground mb-6">
              You don't have a training plan yet. Let's create one personalized for you!
            </p>
            <Button className="bg-amber-500 text-black hover:bg-lime-500" asChild>
              <a href="/onboarding">Create Your Plan</a>
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1">Your Training Plan</h1>
            <p className="text-muted-foreground">
              Version {plan.version} • Created {new Date(plan.createdAt).toLocaleDateString()}
            </p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={async () => await generatePlan()}
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate Plan
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Goal</p>
                <p className="font-medium text-sm">{plan.overview.goal}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Frequency</p>
                <p className="font-medium text-sm">{plan.overview.frequency}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Split</p>
                <p className="font-medium text-sm">{plan.overview.split}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Version</p>
                <p className="font-medium text-sm">v{plan.version}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Notes */}
        <Card className="p-6 mb-8">
          <h2 className="font-semibold text-lg mb-2">Program Notes</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {plan.overview.notes}
          </p>
        </Card>

        {/* Weekly Schedule */}
        <Tabs defaultValue="all" className="mb-8">
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Days</TabsTrigger>
            {plan.weeklySchedule.map((day) => (
              <TabsTrigger key={day.day} value={day.day}>
                {day.day.slice(0, 3)}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="space-y-6">
            {plan.weeklySchedule.map((schedule) => (
              <DayCard key={schedule.day} schedule={schedule} />
            ))}
          </TabsContent>

          {plan.weeklySchedule.map((schedule) => (
            <TabsContent key={schedule.day} value={schedule.day}>
              <DayCard schedule={schedule} />
            </TabsContent>
          ))}
        </Tabs>

        {/* Progression */}
        <Card className="p-6">
          <h2 className="font-semibold text-lg mb-2">Progression Strategy</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {plan.progression}
          </p>
        </Card>
      </div>
    </div>
  );
}
